/**
 * `people` — `PeopleValue` (`specs/M2-tier1-column-types.md` §5.7, `[09§A.7 people]`).
 *
 * `value` uses the **camelCase** key `personsAndTeams` with **numeric** ids, while the typed
 * fragment uses snake_case `persons_and_teams` with **string** ids `[cvs§2 people]`,
 * `[09§A.7 rule 3]`. `changed_at` is absent on 74 of 665 populated cells, so it is never
 * required. `kind` is `person | team | agent`.
 *
 * `toText` joins the display names with `", "` in **stored order** — the recorded corpus has
 * `[{3247457},{3733063}]` -> `"Nico Nuzzi, Roberto"` (`0077-…`). Names are not in the cell, so
 * the caller supplies `ctx.resolveName`; without it `toText` is `""` rather than inventing one.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql, sqlFragment } from '../sql.js';
import { textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const peopleKindSchema = z.enum(['person', 'team', 'agent']);
export const entityRefSchema = z.looseObject({
    id: z.number().int(),
    kind: peopleKindSchema,
});
import { peopleFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const peopleValueSchema = z.looseObject({
    personsAndTeams: z.array(entityRefSchema),
    changed_at: z.string().optional(),
});
export const peopleSettingsSchema = z.looseObject({
    hide_footer: z.boolean().optional(),
    /** A **string**; `"0"` = unlimited `[09§A.7 people]`. */
    max_people_allowed: z.union([z.string(), z.number()]).optional(),
});
export function maxPeopleAllowed(settings) {
    const raw = settings.max_people_allowed;
    if (raw === undefined || raw === null)
        return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}
function readRef(raw, columnType) {
    if (typeof raw === 'number')
        return { id: raw, kind: 'person' };
    if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
        return { id: Number(raw.trim()), kind: 'person' };
    }
    if (typeof raw === 'object' && raw !== null) {
        const record = raw;
        const id = Number(record.id);
        if (!Number.isInteger(id))
            throw new ColumnValueError(columnType, 'entry needs a numeric "id"');
        const kind = typeof record.kind === 'string' ? record.kind : 'person';
        const parsed = peopleKindSchema.safeParse(kind);
        if (!parsed.success)
            throw new ColumnValueError(columnType, `unknown kind "${kind}"`);
        return { id, kind: parsed.data };
    }
    throw new ColumnValueError(columnType, `expected {id, kind}, received ${typeof raw}`);
}
/** Shared by `people` and the deprecated `person` alias, which writes the same shape. */
export function readPeopleList(raw, columnType) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed === '')
            return null;
        // `change_simple_column_value`: `"48202303,331"` `[09§A.7 people]`. The simple form cannot
        // express a team, so every id is read as a person (**ASSUMED**, open question Q175).
        return trimmed.split(',').map((part) => readRef(part.trim(), columnType));
    }
    if (Array.isArray(raw))
        return raw.map((entry) => readRef(entry, columnType));
    if (typeof raw === 'object') {
        const record = raw;
        const list = record.personsAndTeams ?? record.persons_and_teams;
        if (list === null || list === undefined) {
            // The deprecated single-person shape `{"id": N}` `[cvs§2 person]`.
            if ('id' in record)
                return [readRef(record, columnType)];
            return null;
        }
        if (!Array.isArray(list)) {
            throw new ColumnValueError(columnType, '"personsAndTeams" must be an array');
        }
        return list.map((entry) => readRef(entry, columnType));
    }
    throw new ColumnValueError(columnType, `expected an object, received ${typeof raw}`);
}
/** Duplicates are removed on write and preserved on import (M2 §10 case 14). */
export function dedupeRefs(refs) {
    const seen = new Set();
    const out = [];
    for (const ref of refs) {
        const key = `${ref.kind}:${ref.id}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push({ id: ref.id, kind: ref.kind });
    }
    return out;
}
export function joinNames(refs, ctx) {
    const resolve = ctx?.resolveName;
    if (resolve === undefined)
        return '';
    return refs
        .map((ref) => resolve(ref))
        .filter((name) => name !== undefined && name !== '')
        .join(', ');
}
function parsePeople(raw, settings) {
    const list = readPeopleList(raw, 'people');
    if (list === null || list.length === 0)
        return null;
    const refs = dedupeRefs(list);
    const max = maxPeopleAllowed(settings);
    if (max !== null && refs.length > max) {
        throw new ColumnValueError('people', `at most ${max} assignees are allowed on this column`);
    }
    return { personsAndTeams: refs };
}
export const peopleColumn = defineColumnType({
    type: 'people',
    title: 'People',
    description: 'One or more people or teams assigned to the item.',
    apiFragmentName: 'PeopleValue',
    legacyTypeName: 'multiple-person',
    idPrefix: 'people',
    valueSchema: peopleValueSchema,
    settingsSchema: peopleSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: peopleSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    filterConditions: peopleFilterConditions,
    filterOperators: operatorsOf(peopleFilterConditions),
    aggregations: ['avatars', 'count_filled', 'count_empty', 'none'],
    emptyText: '',
    /**
     * 98, not the 140 default: measured on board `174316936` `Executor`, whose stored `width` is
     * `null` and which monday renders at 98 (P-022, re-measured by T-8a).
     */
    defaultWidth: 98,
    parseValue(raw, settings) {
        return parsePeople(raw, settings);
    },
    parseSimpleValue(raw, settings) {
        return parsePeople(raw, settings);
    },
    toText(value, _settings, ctx) {
        if (value === null)
            return '';
        return joinNames(value.personsAndTeams, ctx);
    },
    toSortKeys(value, _settings, ctx) {
        if (value === null)
            return { text: null, number: null, date: null };
        return {
            text: textShadow(joinNames(value.personsAndTeams, ctx)),
            // Number of assignees (**ASSUMED** sort key, open question Q59).
            number: value.personsAndTeams.length,
            date: null,
        };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        const left = a.personsAndTeams;
        const right = b.personsAndTeams;
        // Ordered: the order is user-visible through `toText`.
        return (left.length === right.length &&
            left.every((ref, index) => ref.id === right[index]?.id && ref.kind === right[index]?.kind));
    },
    toActivityValue(value) {
        return value === null ? null : { personsAndTeams: value.personsAndTeams };
    },
    toAssignees(value) {
        if (value === null)
            return [];
        return value.personsAndTeams.map((ref) => ({ id: ref.id, kind: ref.kind }));
    },
    fromMondayValue(raw) {
        const list = readPeopleList(raw, 'people');
        if (list === null)
            return null;
        const value = { personsAndTeams: list };
        if (typeof raw === 'object' && raw !== null) {
            const changedAt = raw.changed_at;
            if (typeof changedAt === 'string')
                value.changed_at = changedAt;
        }
        return value;
    },
    toMondayValue(value) {
        return value === null ? null : { ...value };
    },
    toSimpleValue(value) {
        return value === null ? '' : value.personsAndTeams.map((ref) => String(ref.id)).join(',');
    },
    fromMondaySettings(raw) {
        return peopleSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        return {
            // Ids are **strings** in the fragment and numbers in `value` `[cvs§2 people]`.
            persons_and_teams: value.personsAndTeams.map((ref) => ({ id: String(ref.id), kind: ref.kind })),
            updated_at: toFragmentTimestamp(value.changed_at),
        };
    },
    /**
     * No webhook recording for this type: the conservative pass-through is monday's own `value`
     * object `[whk§7]`, which is what `toMondayValue` already produces (q330).
     */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    /** People filter by person **id** `[cvs§4.7]`; the name is only the label. */
    bucketsOf(value, _settings, ctx) {
        return (value?.personsAndTeams ?? []).map((ref) => ({
            key: ref.id,
            label: ctx?.nameOf?.(ref.id) ?? `#${String(ref.id)}`,
        }));
    },
    /**
     * A kanban drop (`specs/M4-V2-kanban.md` §6.1). People is **multi-select**: the dragged
     * card gains the lane's principal and keeps the ones it had. The kind defaults to
     * `person`; a team lane keeps its kind because the bucket key is only the id, so an
     * existing entry with the same id is left untouched rather than re-kinded.
     */
    valueForBucket(bucketKey, _settings, current) {
        if (!/^\d+$/.test(bucketKey))
            return null;
        const id = Number(bucketKey);
        const existing = current?.personsAndTeams ?? [];
        if (existing.some((ref) => ref.id === id))
            return current;
        return { ...(current ?? {}), personsAndTeams: [...existing, { id, kind: 'person' }] };
    },
    filterSql(ctx) {
        const cell = ctx.refs?.cell ?? 'cv';
        switch (ctx.operator) {
            case 'is_empty':
                return sqlFragment(`${cell}.item_id IS NULL`);
            case 'is_not_empty':
                return sqlFragment(`${cell}.item_id IS NOT NULL`);
            case 'any_of':
                return sqlFragment(`EXISTS (SELECT 1 FROM jsonb_array_elements(${cell}.value->'personsAndTeams') e` +
                    ` WHERE (e->>'id') = ANY(?::text[]))`, [ctx.compareValues.map((value) => String(value))]);
            case 'not_any_of':
                return sqlFragment(`(${cell}.item_id IS NULL OR NOT EXISTS (SELECT 1 FROM` +
                    ` jsonb_array_elements(${cell}.value->'personsAndTeams') e` +
                    ` WHERE (e->>'id') = ANY(?::text[])))`, [ctx.compareValues.map((value) => String(value))]);
            default:
                // `contains` / `doesn't contain` / `starts with` compare the **rendered** assignee
                // names, which is what the `text_value` shadow holds (measured: monday offers all
                // three on a people column).
                return defaultFilterSql('text', ctx);
        }
    },
    summarySql(ctx) {
        if (ctx.aggregation === 'avatars') {
            const cell = ctx.refs?.cell ?? 'cv';
            const fragment = {
                text: `e->>'id' AS id, e->>'kind' AS kind, count(*) FROM LATERAL` +
                    ` jsonb_array_elements(${cell}.value->'personsAndTeams') e GROUP BY 1, 2 ORDER BY 3 DESC`,
                params: [],
                shape: 'buckets',
            };
            return fragment;
        }
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map